import React, { useState, useEffect } from 'react';
import './EmployeeForm.css';
import { useTranslation } from 'react-i18next';
import bcrypt from 'bcryptjs';
import axiosInstance from '../../utils/axiosInstance';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';

const EmployeeForm = ({ onClose, loggedInEmployeeId, onSubmit, employeeToEdit, isSuperAdmin = false }) => {
  const { t } = useTranslation();

  const [employeeData, setEmployeeData] = useState({
    emp_sap_id: '',
    name: '',
    email: '',
    password: '',
    joining_date: '',
    department: '',
    role: '',
    is_active: 1,
    phone_number: '',
    living_address: '',
    current_work_location: '',
    designation: '',
    manager_id: loggedInEmployeeId.toString(),

    // registration_approval profile fields (to match EmployeeInfoModal)
    admission_date: '',
    cost_center: '',
    address: '',
    postal_code: '',
    locality: '',
    nationality: '',
    place_of_birth: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    taxpayer_number: '',
    social_security_number: '',
    beneficiary_number: '',
    private_mobile: '',
    hf_cell_phone: '',
    private_email: '',
    hf_email: '',
    emergency_contact_mobile: '',
    emergency_contact_type: '',
    iban: '',
    swift: '',
    citizen_card_no_passport: '',
    expiry_date_cc_passport: '',
    driving_license_no: '',
    categories: '',
    driving_license_issue_date: '',
    driving_license_validity_date: '',
    no_of_dependents: '',
    hf_function: '',
    profession: '',
    academic_qualifications: '',
    remarks: '',
    company_id: '',
    branch_id: '',
  });

  const [warning, setWarning] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [managerInfo, setManagerInfo] = useState([]);
  const [submitting, setSubmitting] = useState(false); // Track form submission status
  const [existingEmails, setExistingEmails] = useState([]); // Store existing emails fetched from API
  const [currEmp, currEmpSet] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get('/admin/getalldept');
        setDepartments(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    const fetchExistingEmails = async () => {
      try {
        const response = await axiosInstance.get('/admin/employees');
        const emails = response.data.map(employee => employee.p_mail);
        setExistingEmails(emails);
      } catch (error) {
        console.error('Error fetching existing emails:', error);
      }
    };

    const fetchCompanies = async () => {
      try {
        const response = await axiosInstance.get('/user/companies');
        setCompanies(response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchDepartments();
    fetchExistingEmails();
    if (isSuperAdmin) fetchCompanies();
  }, []);

  useEffect(() => {
    const companyId = employeeData.company_id;
    if (!isSuperAdmin || !companyId) return;
    axiosInstance.get(`/user/branches/${companyId}`)
      .then((r) => setBranches(r.data))
      .catch((e) => console.error('Error fetching branches:', e));
  }, [employeeData.company_id, isSuperAdmin]);

  useEffect(() => {
    const companyId = employeeData.company_id;
    if (!isSuperAdmin || !companyId) {
      setManagerInfo([]);
      return;
    }
    axiosInstance.get(`/user/dept_manager-info/${companyId}`)
      .then((r) => setManagerInfo(r.data))
      .catch((e) => console.error('Error fetching manager info:', e));
  }, [employeeData.company_id, isSuperAdmin]);

  useEffect(() => {
    const branchId = employeeData.branch_id;
    if (!isSuperAdmin || !branchId) return;
    axiosInstance.get(`/user/departments/${branchId}`)
      .then((r) => setDepartments(r.data))
      .catch((e) => console.error('Error fetching departments:', e));
  }, [employeeData.branch_id, isSuperAdmin]);

  useEffect(() => {
    if (employeeToEdit) {
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        const month = `0${d.getMonth() + 1}`.slice(-2);
        const day = `0${d.getDate()}`.slice(-2);
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
      };

      setEmployeeData({
        ...employeeData,
        emp_sap_id: currEmp.emp_sap_id ?? employeeToEdit.emp_sap_id ?? '',
        name: currEmp.full_name ?? employeeToEdit.p_name ?? '',
        email: employeeToEdit.emp_mail ?? employeeToEdit.p_mail ?? '',
        password: '', // Password field should not be pre-filled
        joining_date: currEmp.admission_date ? formatDate(currEmp.admission_date) : '',
        department: currEmp.dept_id ?? '',
        role: employeeToEdit.emp_role ?? '',
        is_active: employeeToEdit.is_active ?? 1,
        phone_number: employeeToEdit.p_contact ?? '',
        living_address: employeeToEdit.p_address ?? '',
        current_work_location: employeeToEdit.work_location ?? '',
        designation: currEmp.profession ?? '',
        manager_id: String(employeeToEdit.manager_id ?? loggedInEmployeeId)
      });
    }
  }, [employeeToEdit, loggedInEmployeeId, currEmp]);

  useEffect(() => {
    if (employeeToEdit) {
      const fetchEmployeeInfo = async () => {
        try {
          setLoadingProfile(true);
          const response = await axiosInstance.get(`/admin/employeeInfo/${employeeToEdit.emp_id}`);
          currEmpSet(response.data);
        } catch (error) {
          console.error('Error fetching employee info:', error);
        } finally {
          setLoadingProfile(false);
        }
      };

      fetchEmployeeInfo();
    }
  }, [employeeToEdit]);

  useEffect(() => {
    if (!employeeToEdit || !currEmp) return;
    const toDateInput = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    };

    setEmployeeData((prev) => ({
      ...prev,
      emp_sap_id: currEmp.emp_sap_id ?? prev.emp_sap_id,
      admission_date: toDateInput(currEmp.admission_date),
      cost_center: currEmp.cost_center ?? '',
      address: currEmp.address ?? '',
      postal_code: currEmp.postal_code ?? '',
      locality: currEmp.locality ?? '',
      nationality: currEmp.nationality ?? '',
      place_of_birth: currEmp.place_of_birth ?? '',
      date_of_birth: toDateInput(currEmp.date_of_birth),
      gender: currEmp.gender ?? '',
      marital_status: currEmp.marital_status ?? '',
      taxpayer_number: currEmp.taxpayer_number ?? '',
      social_security_number: currEmp.social_security_number ?? '',
      beneficiary_number: currEmp.beneficiary_number ?? '',
      private_mobile: currEmp.private_mobile ?? '',
      hf_cell_phone: currEmp.hf_cell_phone ?? '',
      private_email: currEmp.private_email ?? '',
      hf_email: currEmp.hf_email ?? '',
      emergency_contact_mobile: currEmp.emergency_contact_mobile ?? '',
      emergency_contact_type: currEmp.emergency_contact_type ?? '',
      iban: currEmp.iban ?? '',
      swift: currEmp.swift ?? '',
      citizen_card_no_passport: currEmp.citizen_card_no_passport ?? '',
      expiry_date_cc_passport: toDateInput(currEmp.expiry_date_cc_passport),
      driving_license_no: currEmp.driving_license_no ?? '',
      categories: currEmp.categories ?? '',
      driving_license_issue_date: toDateInput(currEmp.driving_license_issue_date),
      driving_license_validity_date: toDateInput(currEmp.driving_license_validity_date),
      no_of_dependents: currEmp.no_of_dependents ?? '',
      hf_function: currEmp.hf_function ?? '',
      profession: currEmp.profession ?? '',
      academic_qualifications: currEmp.academic_qualifications ?? '',
      remarks: currEmp.remarks ?? '',
      company_id: currEmp.company_id ?? '',
      branch_id: currEmp.branch_id ?? '',
      department: currEmp.dept_id ?? prev.department,
      name: currEmp.full_name ?? prev.name,
      designation: currEmp.profession ?? prev.designation,
    }));
  }, [currEmp, employeeToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    // Disable submit button to prevent multiple submissions
    setSubmitting(true);

    // Determine required fields based on edit or add mode
    let requiredFields;
    if (employeeToEdit) {
      requiredFields = ["name", "email", "department", "phone_number", "living_address", "current_work_location"];
      if (isSuperAdmin) requiredFields.push("role");
    } else {
      requiredFields = ["name", "email", "password", "joining_date", "department", "role", "phone_number", "living_address", "current_work_location"];
    }

    // Filter out fields that are undefined or empty after trimming
    const emptyFields = requiredFields.filter(field => {
      const value = employeeData[field];
      return value === undefined || value === null || (typeof value === 'string' && !value.trim());
    });
    if (emptyFields.length > 0) {
      // Display warning message if any required field is empty
      setWarning(`Please fill in all fields: ${emptyFields.join(', ')}`);
      setSubmitting(false); // Enable submit button
      return;
    }

    try {
      // NOTE: EmployeeForm should not call APIs directly.
      // The parent container decides which endpoint to hit (admin vs superadmin).
      // Here we only normalize fields and pass up.
      const dataToSend = {
        ...employeeData,
        is_active: Number(employeeData.is_active),
        manager_id: employeeData.manager_id ? Number(employeeData.manager_id) : null,
      };

      // Check if the email already exists when adding a new employee
      if (!employeeToEdit && existingEmails.includes(employeeData.email)) {
        setWarning('Email already exists. Please use a different email.');
        setSubmitting(false);
        return;
      }

      if (!employeeToEdit) {
        dataToSend.password = await bcrypt.hash(employeeData.password, 10);
      } else {
        // Never submit empty password on edit
        delete dataToSend.password;
      }

      await onSubmit(dataToSend);
      onClose();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Error saving employee');
    } finally {
      setSubmitting(false); // Enable submit button after submission completes
    }
  };

  return (
    <Modal
      show
      onHide={onClose}
      centered
      size="lg"
      dialogClassName="employeeFormModal"
      backdrop="static"
      keyboard={!submitting}
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>{employeeToEdit ? t("empForm_editTitle") : t("empForm_addTitle")}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="employeeFormModalBody">
        {loadingProfile ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Form id="employeeEditForm" onSubmit={handleSubmit}>
            {warning && <Alert variant="warning">{warning}</Alert>}

            {!employeeToEdit && (
              <Row>
                <Col md={12} className="mb-2">
                  <Form.Group>
                    <Form.Label>{t("empForm_pass")}</Form.Label>
                    <div className="password-input-wrapper">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder={t("empForm_passPlace")}
                        value={employeeData.password}
                        onChange={handleChange}
                        required
                      />
                      <i
                        className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                        onClick={() => setShowPassword(!showPassword)}
                      ></i>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <h6 className="section-title">{t("Personal Information")}</h6>
            <Row>
              {isSuperAdmin && employeeToEdit && (
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>{t("Employee SAP ID")}</Form.Label>
                    <Form.Control name="emp_sap_id" value={employeeData.emp_sap_id} disabled />
                  </Form.Group>
                </Col>
              )}
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("attendance_name")}</Form.Label>
                  <Form.Control name="name" value={employeeData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_join")}</Form.Label>
                  <Form.Control type="date" name="joining_date" value={employeeData.joining_date} onChange={handleChange} required />
                </Form.Group>
              </Col>

              {isSuperAdmin && employeeToEdit && (
                <>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Date of Birth")}</Form.Label>
                      <Form.Control type="date" name="date_of_birth" value={employeeData.date_of_birth} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Admission Date")}</Form.Label>
                      <Form.Control type="date" name="admission_date" value={employeeData.admission_date} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Gender")}</Form.Label>
                      <Form.Control as="select" name="gender" value={employeeData.gender} onChange={handleChange}>
                        <option value="">{t("Select Gender")}</option>
                        <option value="Male">{t("Male")}</option>
                        <option value="Female">{t("Female")}</option>
                        <option value="Other">{t("Other")}</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Marital Status")}</Form.Label>
                      <Form.Control as="select" name="marital_status" value={employeeData.marital_status} onChange={handleChange}>
                        <option value="">{t("Select Martial Status")}</option>
                        <option value="Single">{t("Single")}</option>
                        <option value="Married">{t("Married")}</option>
                        <option value="Divorced">{t("Divorced")}</option>
                        <option value="Widowed">{t("Widowed")}</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Nationality")}</Form.Label>
                      <Form.Control name="nationality" value={employeeData.nationality} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Place of Birth")}</Form.Label>
                      <Form.Control name="place_of_birth" value={employeeData.place_of_birth} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Taxpayer Number")}</Form.Label>
                      <Form.Control name="taxpayer_number" value={employeeData.taxpayer_number} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Social Security Number")}</Form.Label>
                      <Form.Control name="social_security_number" value={employeeData.social_security_number} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>

            <h6 className="section-title mt-3">{t("Professional Information")}</h6>
            <Row>
              {isSuperAdmin && employeeToEdit && (
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>{t("Profession")}</Form.Label>
                    <Form.Control name="profession" value={employeeData.profession} onChange={handleChange} />
                  </Form.Group>
                </Col>
              )}
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_design")}</Form.Label>
                  <Form.Control name="designation" value={employeeData.designation} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_dept")}</Form.Label>
                  <Form.Control as="select" name="department" value={employeeData.department} onChange={handleChange} required>
                    <option value="">{t("empForm_deptPlace")}</option>
                    {departments.map((dept) => (
                      <option key={dept.dept_id} value={dept.dept_id}>{dept.dept_name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              {isSuperAdmin && employeeToEdit && (
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>{t("Manager Name")}</Form.Label>
                    <Form.Control
                      as="select"
                      name="manager_id"
                      value={String(employeeData.manager_id ?? '')}
                      onChange={handleChange}
                      disabled={!employeeData.company_id}
                    >
                      <option value="">{t("Select Manager")}</option>
                      {managerInfo.map((m) => (
                        <option key={m.emp_id} value={m.emp_id}>{m.p_name}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}

              {(isSuperAdmin || !employeeToEdit) && (
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>{t("empForm_role")}</Form.Label>
                    <Form.Control as="select" name="role" value={employeeData.role} onChange={handleChange} required>
                      <option value="">{t("empForm_rolePlace")}</option>
                      <option value="User">User</option>
                      <option value="Admin">Admin</option>
                      {isSuperAdmin && <option value="SuperAdmin">SuperAdmin</option>}
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}

              {isSuperAdmin && (
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Active</Form.Label>
                    <Form.Control as="select" name="is_active" value={String(employeeData.is_active)} onChange={handleChange}>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}

              {isSuperAdmin && employeeToEdit && (
                <>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Company")}</Form.Label>
                      <Form.Control as="select" name="company_id" value={employeeData.company_id} onChange={handleChange}>
                        <option value="">{t("Select Company")}</option>
                        {companies.map((c) => (
                          <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Branch")}</Form.Label>
                      <Form.Control as="select" name="branch_id" value={employeeData.branch_id} onChange={handleChange} disabled={!employeeData.company_id}>
                        <option value="">{t("Select Branch")}</option>
                        {branches.map((b) => (
                          <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Cost Center")}</Form.Label>
                      <Form.Control name="cost_center" value={employeeData.cost_center} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("HF Function")}</Form.Label>
                      <Form.Control name="hf_function" value={employeeData.hf_function} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Academic Qualifications")}</Form.Label>
                      <Form.Control name="academic_qualifications" value={employeeData.academic_qualifications} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Remarks")}</Form.Label>
                      <Form.Control name="remarks" value={employeeData.remarks} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>

            <h6 className="section-title mt-3">{t("Contact Information")}</h6>
            <Row>
              {isSuperAdmin && employeeToEdit && (
                <>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Address")}</Form.Label>
                      <Form.Control name="address" value={employeeData.address} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Postal Code")}</Form.Label>
                      <Form.Control name="postal_code" value={employeeData.postal_code} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Locality")}</Form.Label>
                      <Form.Control name="locality" value={employeeData.locality} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </>
              )}
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_phoneNo")}</Form.Label>
                  <Form.Control name="phone_number" value={employeeData.phone_number} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" name="email" value={employeeData.email} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_living")}</Form.Label>
                  <Form.Control name="living_address" value={employeeData.living_address} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>{t("empForm_workLoc")}</Form.Label>
                  <Form.Control name="current_work_location" value={employeeData.current_work_location} onChange={handleChange} required />
                </Form.Group>
              </Col>

              {isSuperAdmin && employeeToEdit && (
                <>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Private Mobile")}</Form.Label>
                      <Form.Control name="private_mobile" value={employeeData.private_mobile} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("HF Cell Phone")}</Form.Label>
                      <Form.Control name="hf_cell_phone" value={employeeData.hf_cell_phone} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Private Email")}</Form.Label>
                      <Form.Control type="email" name="private_email" value={employeeData.private_email} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("HF Email")}</Form.Label>
                      <Form.Control type="email" name="hf_email" value={employeeData.hf_email} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Emergency Contact (Mobile)")}</Form.Label>
                      <Form.Control name="emergency_contact_mobile" value={employeeData.emergency_contact_mobile} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Emergency Contact (Type)")}</Form.Label>
                      <Form.Control name="emergency_contact_type" value={employeeData.emergency_contact_type} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>

            {isSuperAdmin && employeeToEdit && (
              <>
                <h6 className="section-title mt-3">{t("Accounts Information")}</h6>
                <Row>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>IBAN</Form.Label>
                      <Form.Control name="iban" value={employeeData.iban} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>SWIFT</Form.Label>
                      <Form.Control name="swift" value={employeeData.swift} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Beneficiary Number")}</Form.Label>
                      <Form.Control name="beneficiary_number" value={employeeData.beneficiary_number} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("No. of Dependents")}</Form.Label>
                      <Form.Control type="number" name="no_of_dependents" value={employeeData.no_of_dependents} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="section-title mt-3">{t("Additional Information")}</h6>
                <Row>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Citizen Card No. / Passport")}</Form.Label>
                      <Form.Control name="citizen_card_no_passport" value={employeeData.citizen_card_no_passport} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Expiry CC / Passport")}</Form.Label>
                      <Form.Control type="date" name="expiry_date_cc_passport" value={employeeData.expiry_date_cc_passport} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Driving License No.")}</Form.Label>
                      <Form.Control name="driving_license_no" value={employeeData.driving_license_no} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Categories")}</Form.Label>
                      <Form.Control name="categories" value={employeeData.categories} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Driving License Issue")}</Form.Label>
                      <Form.Control type="date" name="driving_license_issue_date" value={employeeData.driving_license_issue_date} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Group>
                      <Form.Label>{t("Driving License Validity")}</Form.Label>
                      <Form.Control type="date" name="driving_license_validity_date" value={employeeData.driving_license_validity_date} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t("projectDetails_back") || 'Back'}
        </Button>
        <Button variant="primary" type="submit" form="employeeEditForm" disabled={submitting}>
          {submitting ? t("empForm_submitting") : t("empForm_submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmployeeForm;
